import { OverlayTriggerHandle } from "rsuite/esm/internals/Overlay/OverlayTrigger";
import { Col, Collapse, Row } from "reactstrap";
import { Popover, Whisper } from "rsuite";
import { useRef, useState } from "react";

import UpdateRecordDrawer from "../Drawers/Records/UpdateRecordDrawer";
import IndeterminateCheckbox from "../AppTable/IndeterminateCheckbox";
import RecordActionsMenu from "../Drawers/Records/RecordActionsMenu";
import { ReusableViewIDs } from "../../types/PageInterfaces";
import ColumnAdd from "../AppTable/ColumnAdd/ColumnAdd";
import RecordNameCell from "../Records/RecordNameCell";
import TableListItemInfo from "./TableListItemInfo";
import useToggle from "../../hooks/useToggle";
import Divider from "../../elements/Divider";

const hiddenHeaders = ["id"];

interface Props {
  item: any;
  module: any;
  selectedRecords: Array<any>;
  selectableRows: boolean;
  imageField: string | null;
  imageFieldPresentation: "Logo" | "Avatar" | null;
  metadataField: string | null;
  metadataFieldType: string | null;
  separateName: boolean;
  multiSubitemFieldAPIName: string;
  subitemParentID: string;
  fields: any;
  columnAdd?: boolean;
  columnAddMessage: string | null;
  index: number;
  nameField?: string;
  onOpenRecordName?: (data: any) => void;
  onSelect: (val: any) => void;
  onDeselect: (val: any) => void;
  onColumnAdd: (val: any) => void;
  onColumnRemove: (val: any) => void;
  reusableViews?: ReusableViewIDs;
}

const AppTableListItem = ({
  item,
  onOpenRecordName,
  module,
  selectedRecords,
  onSelect,
  onDeselect,
  selectableRows,
  imageField,
  imageFieldPresentation,
  metadataField,
  metadataFieldType,
  separateName,
  multiSubitemFieldAPIName,
  subitemParentID,
  fields,
  columnAdd = false,
  columnAddMessage,
  onColumnAdd,
  onColumnRemove,
  index,
  nameField,
  reusableViews,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [updateRecordDrawer, toggleUpdateRecordDrawer] = useToggle();

  const onColumnAddRef = useRef<(val: any) => void>();
  const onColumnRemoveRef = useRef<(val: any) => void>();
  const selected = selectedRecords[index] ? true : false;
  onColumnAddRef.current = onColumnAdd;
  onColumnRemoveRef.current = onColumnRemove;
  const actionsRef = useRef<OverlayTriggerHandle>(null);

  return (
    <>
      <Divider css="m-0" />
      <Row
        className={`d-flex justify-content-center align-items-center m-0 p-0 ${
          selected && "bg-primary-selected"
        }`}
        style={{
          height: "45px",
          backgroundColor: open ? "#F2F6F4" : "",
        }}
      >
        <Col xs={1} className="m-0 p-0" onClick={() => setOpen(!open)}>
          <i
            className={`mdi mdi-chevron-${open ? "down" : "right"}`}
            style={{
              fontSize: 25,
            }}
          ></i>
        </Col>
        {selectableRows && (
          <Col xs={1} className="m-0 p-0">
            <IndeterminateCheckbox
              checked={selected}
              onChange={selected ? onDeselect : onSelect}
              indeterminate={undefined}
            />
          </Col>
        )}
        <Col xs={selectableRows ? 9 : 10} className="m-0 p-0">
          <RecordNameCell
            record={item}
            onOpen={() => {
              onOpenRecordName &&
                onOpenRecordName({
                  recordID: item?.id,
                  moduleAPIName: module?.APIName,
                  tab: reusableViews?.RecordDetail
                    ? `Reuse-${reusableViews?.RecordDetail}-Summary`
                    : "Summary",
                });
            }}
            disableViewRecord={onOpenRecordName ? true : false}
            imageField={imageField}
            imageFieldPresentation={imageFieldPresentation}
            metadataField={metadataField}
            metadataFieldType={metadataFieldType}
            nameField={nameField}
          />
        </Col>
        {!module?.Dynamic && (
          <Col xs={1} className="m-0 p-0">
            <Whisper
              placement="autoVerticalEnd"
              trigger="click"
              ref={actionsRef}
              speaker={
                <Popover
                  className="sidebar-popover"
                  onClick={(e: any) => e.stopPropagation()}
                >
                  <RecordActionsMenu
                    record={item}
                    toggleUpdateRecordDrawer={toggleUpdateRecordDrawer}
                    actionsRef={actionsRef}
                    module={module}
                  />
                </Popover>
              }
            >
              <i className="mdi mdi-dots-horizontal" style={{ fontSize: 25 }} />
            </Whisper>
          </Col>
        )}
      </Row>
      {open && (
        <Collapse isOpen={open}>
          <TableListItemInfo
            item={item}
            subitemParentID={subitemParentID}
            moduleAPIName={module?.APIName}
            multiSubitemFieldAPIName={multiSubitemFieldAPIName}
            separateName={separateName}
            fields={fields}
            reusableViews={reusableViews}
          />
          {columnAdd && (
            <div className="d-flex justify-content-center">
              <ColumnAdd
                fields={fields}
                moduleAPIName={module.APIName}
                hiddenFields={hiddenHeaders}
                onColumnAdd={onColumnAddRef.current}
                description={columnAddMessage}
                multiSubitemFieldAPIName={multiSubitemFieldAPIName}
              />
            </div>
          )}
        </Collapse>
      )}
      <UpdateRecordDrawer
        open={updateRecordDrawer}
        onClose={toggleUpdateRecordDrawer}
        recordID={item.id}
        moduleAPIName={module?.APIName}
        layout={null}
      />
    </>
  );
};

export default AppTableListItem;
